import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { slugify } from '../shared/testid/testid.util';

// The coverage the form describes: the pre-scoped service key, the list of
// sub-type keys this document blankets (the scoped node plus any checked
// siblings), and the raw expiration date string (yyyy-MM-dd, or '' for none).
// Consumers convert `expirationDate` to whatever their save call expects.
export interface ZoneDocCoverage {
  serviceType: string | null;
  serviceSubTypes: string[];
  expirationDate: string;
  // yyyy-MM-dd string (or '' for none) — captured only when the license requires
  // an issue date. Optional so pre-license consumers (which never read it) stay
  // source-compatible.
  issueDate?: string;
  // The named LicenseRequirement this coverage satisfies (echoed back on every
  // emit so a coverage edit — which is a full replacement server-side — preserves
  // the link). null when the document is not tied to a requirement.
  licenseRequirementId?: string | null;
}

// Reusable coverage-context + expiration picker shared by the zone-document
// upload dialog and the edit/link dialog. The service it emits coverage for is
// always pre-scoped by the parent (the clicked node in the upload dialog; the
// requirement-derived node in the edit dialog) — this form never offers a free
// service dropdown. It renders:
//
//  - a read-only service chip plus "also apply to sub-services" checkboxes for
//    the sibling sub-services (upload dialog), gated by `showServiceContext`; and
//  - the expiration (and, when the license requires it, issue) date fields.
//
// The edit/link dialog owns its own requirement + "Applies to" node UI and hides
// this form's service chip via `showServiceContext = false`, feeding the derived
// node in purely so the emitted coverage carries the right serviceType/sub-type.
// It owns no file/preview concern.
@Component({
  standalone: true,
  selector: 'zone-doc-coverage-form',
  imports: [CommonModule, FormsModule, MatDatepickerModule, MatNativeDateModule],
  templateUrl: './zone-doc-coverage-form.component.html',
  styleUrl: './zone-doc-coverage-form.component.scss',
})
export class ZoneDocCoverageFormComponent implements OnInit, OnChanges {
  // The pre-scoped service (read-only chip). Provides `type` + `displayName`.
  @Input() service: any = null;
  // The sub-service node the action was scoped to, or null for a service-level
  // node. Its type key is always included in the emitted coverage.
  @Input() scopedSubType: any = null;
  // Other enabled, document-requiring sub-services under the same service; each
  // becomes an "also apply to" checkbox. Shape: { type, displayName, checked? }.
  @Input() siblingSubTypes: any[] = [];
  // Initial expiration date as a yyyy-MM-dd string (or '' for none).
  @Input() initialExpiration: string = '';
  // Initial issue date as a yyyy-MM-dd string (or '' for none).
  @Input() initialIssueDate: string = '';

  // --- License-driven date prompting ----------------------------------------
  // When true, the (existing) expiration field is shown as required; when true,
  // a dedicated Issue Date field is shown as required. Both default false so the
  // pre-license upload/edit flows render no date-requirement chrome. Driven by
  // the license requirement's flags.
  @Input() expirationDateRequired = false;
  @Input() issueDateRequired = false;
  // The named LicenseRequirement this coverage is for. Echoed back verbatim on
  // every emit so a full-replacement coverage edit preserves the link.
  @Input() licenseRequirementId: string | null = null;
  // Whether the read-only service chip (+ its "also apply to" checkboxes) renders
  // at all. The upload dialog leaves it true; the edit/link dialog sets it false
  // and shows its own requirement-derived "Applies to" context instead.
  @Input() showServiceContext = true;
  // When true, date fields follow the license flags strictly: the expiration
  // field is shown only when expirationDateRequired. When false (default, legacy
  // + internal + the edit dialog), the expiration field always shows so an
  // expiration can be edited regardless of a license. The Issue Date field is
  // shown only when issueDateRequired in either case.
  @Input() licenseDriven = false;

  // Whether the expiration field renders at all (always in legacy mode; only when
  // required in strict license-driven mode).
  get showExpiration(): boolean {
    return !this.licenseDriven || this.expirationDateRequired;
  }

  // Whether the issue-date field renders at all. Mirrors `showExpiration`: always
  // in legacy mode (the edit dialog is not licenseDriven, so an issue date can be
  // set/edited regardless of a license), only when required in strict
  // license-driven mode (the upload dialog). The required asterisk is driven
  // separately by `issueDateRequired`, so the field can render as optional.
  get showIssue(): boolean {
    return !this.licenseDriven || this.issueDateRequired;
  }

  // Emitted on init and on every change so the parent always holds the latest
  // coverage without reaching into the form.
  @Output() coverageChange = new EventEmitter<ZoneDocCoverage>();

  // Local mutable copies so the form never mutates its @Input arrays/values.
  siblings: any[] = [];
  // The expiration bound to the Material datepicker. Held as a `Date` (or null
  // for none) because MatDatepicker binds a Date; it is converted back to the
  // yyyy-MM-dd string the pipeline expects on every emit (see `emit`).
  expirationDate: Date | null = null;
  // The issue date bound to its Material datepicker (or null for none), converted
  // to a yyyy-MM-dd string on every emit exactly like expirationDate.
  issueDate: Date | null = null;
  // The service driving the emitted coverage — always the pre-scoped `service`.
  activeService: any = null;

  // Reference to the native expiration input. MatDatepickerInput already nulls
  // out `expirationDate` when what's typed/pasted doesn't parse to a valid
  // date (see `onExpirationBlur`/`onExpirationPaste` below) - but that happens
  // via its CVA callback, which is view-to-model only, so the leftover invalid
  // text is never written back into the field. We reach into the DOM directly
  // to clear it.
  @ViewChild('expirationInput') private expirationInputRef?: ElementRef<HTMLInputElement>;
  // Same DOM-clearing reference for the issue-date input.
  @ViewChild('issueInput') private issueInputRef?: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.syncFromInputs();
    this.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A structural change means the parent reopened the form for a different
    // node/document — fully re-scope from the inputs.
    const structuralChange =
      changes['service'] ||
      changes['scopedSubType'] ||
      changes['siblingSubTypes'] ||
      changes['initialExpiration'] ||
      changes['initialIssueDate'];
    if (structuralChange) {
      this.syncFromInputs();
      this.emit();
    } else if (changes['licenseRequirementId']) {
      // Only the "applies to requirement" selector changed. Echo the new id back
      // WITHOUT re-running syncFromInputs — that would reset the user's
      // in-progress expiration/issue/sub-type edits to their initials.
      this.emit();
    }
  }

  onChange(): void {
    this.emit();
  }

  // Blocks any typed character that isn't a digit or the date separator.
  // Keys with a multi-character `key` name (Backspace, Delete, Tab, the arrow
  // keys, Home, End, Enter, Shift, ...) never insert a character, so they pass
  // through untouched, as do Ctrl/Cmd+A/C/V/X clipboard shortcuts.
  onExpirationKeydown(event: KeyboardEvent): void {
    if (event.key.length !== 1) {
      return;
    }
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())
    ) {
      return;
    }
    if (!/[0-9/]/.test(event.key)) {
      event.preventDefault();
    }
  }

  // Typed or pasted text the datepicker can't parse already leaves
  // `expirationDate` null (see the class comment on `expirationInputRef`); this
  // clears the leftover text itself so the field never shows garbage once the
  // user leaves it.
  onExpirationBlur(): void {
    this.clearInvalidExpirationText();
  }

  // Same guard for paste: run it a tick after the paste's default insertion
  // and the datepicker's own (ngModelChange) parsing have both landed.
  onExpirationPaste(): void {
    setTimeout(() => this.clearInvalidExpirationText());
  }

  private clearInvalidExpirationText(): void {
    if (this.expirationDate) {
      return;
    }
    if (this.expirationInputRef) {
      this.expirationInputRef.nativeElement.value = '';
    }
    this.emit();
  }

  // The issue-date field reuses the exact same typing/paste guards as expiration.
  onIssueKeydown(event: KeyboardEvent): void {
    this.onExpirationKeydown(event);
  }

  onIssueBlur(): void {
    this.clearInvalidIssueText();
  }

  onIssuePaste(): void {
    setTimeout(() => this.clearInvalidIssueText());
  }

  private clearInvalidIssueText(): void {
    if (this.issueDate) {
      return;
    }
    if (this.issueInputRef) {
      this.issueInputRef.nativeElement.value = '';
    }
    this.emit();
  }

  // Stable testid part for a sibling sub-service checkbox, keyed on its
  // `type` (not the user-visible `displayName`).
  siblingTestKey(sibling: any): string {
    return slugify(sibling?.type || sibling?.displayName || 'sub-service');
  }

  private syncFromInputs(): void {
    this.activeService = this.service;
    this.siblings = (this.siblingSubTypes ?? []).map((st: any) => ({
      type: st.type,
      displayName: st.displayName,
      checked: !!st.checked,
    }));
    this.expirationDate = this.parseExpiration(this.initialExpiration);
    this.issueDate = this.parseExpiration(this.initialIssueDate);
  }

  private emit(): void {
    const serviceSubTypes: string[] = [];
    if (this.scopedSubType?.type) {
      serviceSubTypes.push(this.scopedSubType.type);
    }
    for (const sibling of this.siblings) {
      if (sibling.checked) {
        serviceSubTypes.push(sibling.type);
      }
    }
    this.coverageChange.emit({
      serviceType: this.activeService?.type ?? null,
      serviceSubTypes,
      expirationDate: this.formatExpiration(this.expirationDate),
      issueDate: this.formatExpiration(this.issueDate),
      licenseRequirementId: this.licenseRequirementId ?? null,
    });
  }

  // Parses a yyyy-MM-dd prefill string into a Date at LOCAL midnight, or null for
  // empty/malformed input. Building the Date from explicit y/m/d parts (rather
  // than `new Date(str)`, which parses yyyy-MM-dd as UTC) keeps the calendar on
  // the intended day in every timezone.
  private parseExpiration(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const parts = value.split('-');
    if (parts.length !== 3) {
      return null;
    }
    const [year, month, day] = parts.map((p) => Number(p));
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  // Formats the datepicker's Date back into the yyyy-MM-dd string the rest of the
  // pipeline expects, or '' for none. Reads the LOCAL y/m/d components (not
  // toISOString, which would shift across the UTC boundary) so the emitted string
  // round-trips losslessly with `parseExpiration`.
  private formatExpiration(date: Date | null): string {
    if (!date) {
      return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
